import {
  TileSpecification,
  TileType,
} from 'src/queries/agent/network/agent.models'

export const MAX_PINNED_INSIGHTS = 5
const STORAGE_PREFIX = 'extend:pinnedInsights'

export interface PinnedInsightStored {
  spec: TileSpecification
  title: string
  pinnedAt: string
  key: string
}

const isBrowser = typeof window !== 'undefined'

const getStorageKey = (userId: string): string => {
  return `${STORAGE_PREFIX}:user:${userId}`
}

function getDefaultPinnedInsights(): PinnedInsightStored[] {
  const spec = {
    type: TileType.NUMBER_CARD,
    title: 'Month Over Month Spend',
    endpoint: '/clickhouse/insights/spendvspriorperiod',
    method: 'GET',
    params: {
      since: null,
      until: null,
      dateType: null,
      expenseCategoryId: null,
      expenseLabelId: null,
      merchantCategory: null,
      creditCardId: null,
      virtualCardId: null,
      recipientId: null,
      status: null,
      source: null,
      receiptStatus: null,
      departments: null,
      comparisonType: 'MOM',
      interval: null,
    },
    config: {
      valueKey: 'summary.currentPeriod.totalUsdSpendCents',
      format: 'currency',
      comparisonKey: 'summary.priorPeriod.totalUsdSpendCents',
    },
    promptContext: '',
  } as TileSpecification

  return [
    {
      spec,
      title: spec.title,
      pinnedAt: new Date().toISOString(),
      key: buildPinnedInsightKey(spec),
    },
  ]
}

export const buildPinnedInsightKey = (spec: TileSpecification): string => {
  const paramsJson = JSON.stringify(spec.params ?? {})
  return [
    spec.type,
    spec.endpoint,
    paramsJson,
    spec.promptContext ?? '',
    spec.title ?? '',
  ].join('|')
}

export const loadPinnedInsights = (
  userId?: string | null,
): PinnedInsightStored[] => {
  if (!isBrowser) {
    return []
  }

  try {
    if (!userId) {
      return []
    }
    const storageKey = getStorageKey(userId)
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) {
      const defaults = getDefaultPinnedInsights()
      if (defaults.length > 0) {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(defaults))
        } catch {
          // ignore write errors, still return default seed
        }
      }
      return defaults
    }
    const parsed = JSON.parse(raw) as PinnedInsightStored[]
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed
      .filter((item): item is PinnedInsightStored => {
        return !!item?.spec?.endpoint && !!item?.key
      })
      .slice(0, MAX_PINNED_INSIGHTS)
  } catch {
    return []
  }
}

export const persistPinnedInsights = (
  userId: string | null | undefined,
  insights: PinnedInsightStored[],
): boolean => {
  if (!isBrowser || !userId) {
    return false
  }

  try {
    window.localStorage.setItem(
      getStorageKey(userId),
      JSON.stringify(insights.slice(0, MAX_PINNED_INSIGHTS)),
    )
    return true
  } catch {
    return false
  }
}
